import React, { useMemo, useState, useEffect } from "react";
import {
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Input,
  Button,
  Box,
  Flex,
  Text,
  IconButton,
  useToast,
} from "@chakra-ui/react";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  SearchIcon,
  DownloadIcon,
} from "@chakra-ui/icons";
import axiosInstance from "../api/axiosConfig";
import * as XLSX from "xlsx";

const VehicleLocationsTable = ({
  dayData = [],
  nightData = [],
  groupLevel,
  colors,
  selectedLocation,
  onRowSelect,
}) => {
  const toast = useToast();
  // Estado para la tabla de datos completos
  const [completeData, setCompleteData] = useState({
    data: [],
    total: 0,
    currentPage: 1,
    totalPages: 1,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Combinar datos día/noche para la primera pestaña
  const combinedDict = useMemo(() => {
    const dict = {};
    dayData.forEach((item) => {
      const key = item.level || "SIN_DIA";
      if (!dict[key]) dict[key] = { day: 0, night: 0 };
      dict[key].day += Number(item.total_vehiculos || 0);
    });
    nightData.forEach((item) => {
      const key = item.level || "SIN_NOCHE";
      if (!dict[key]) dict[key] = { day: 0, night: 0 };
      dict[key].night += Number(item.total_vehiculos || 0);
    });
    return dict;
  }, [dayData, nightData]);

  // Convertir a array y ordenar para la primera pestaña
  const dataSource = useMemo(() => {
    return Object.keys(combinedDict)
      .map((key) => ({
        name: key,
        day: combinedDict[key].day,
        night: combinedDict[key].night,
        total: combinedDict[key].day + combinedDict[key].night,
      }))
      .sort((a, b) => b.total - a.total);
  }, [combinedDict]);

  // Cargar datos completos
  const fetchCompleteData = async (page = 1, search = "") => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.get(
        "/vehicle-locations-cantons/complete",
        {
          params: { page, limit: 10, search },
        }
      );
      setCompleteData(response.data);
    } catch (error) {
      console.error("Error al cargar datos completos:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Efecto para cargar datos iniciales
  useEffect(() => {
    fetchCompleteData(1, "");
  }, []);

  // Manejadores de eventos
  const handleSearch = () => {
    setCurrentPage(1);
    fetchCompleteData(1, searchTerm);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    fetchCompleteData(newPage, searchTerm);
  };

  // Función para exportar datos del resumen
  const handleExportSummary = () => {
    try {
      const exportData = dataSource.map((row) => ({
        [groupLevel.toUpperCase()]: row.name,
        Diurno: row.day,
        Nocturno: row.night,
        Total: row.total,
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Resumen");

      // Guardar el archivo
      XLSX.writeFile(
        wb,
        `resumen_${groupLevel}_${new Date().toISOString().split("T")[0]}.xlsx`
      );

      toast({
        title: "Exportación exitosa",
        description: "Los datos se han exportado correctamente",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error al exportar:", error);
      toast({
        title: "Error al exportar",
        description: "No se pudieron exportar los datos",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Función para exportar datos completos
  const handleExportComplete = async () => {
    setIsExporting(true);
    try {
      // Obtener todos los datos (sin paginación)
      const response = await axiosInstance.get(
        "/vehicle-locations-cantons/complete",
        {
          params: { page: 1, limit: 10000, search: searchTerm },
        }
      );

      const exportData = response.data.data.map((row) => ({
        "Chasis (VIN)": row.vin,
        "Provincia (Día)": row.day_province,
        "Provincia (Noche)": row.night_province,
        "Cantón (Día)": row.day_canton,
        "Cantón (Noche)": row.night_canton,
        "Parroquia (Día)": row.day_parroquia,
        "Parroquia (Noche)": row.night_parroquia,
        "Latitud (Día)": row.day_lat,
        "Longitud (Día)": row.day_lng,
        "Latitud (Noche)": row.night_lat,
        "Longitud (Noche)": row.night_lng,
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Datos Completos");

      // Guardar el archivo
      XLSX.writeFile(
        wb,
        `datos_completos_${new Date().toISOString().split("T")[0]}.xlsx`
      );

      toast({
        title: "Exportación exitosa",
        description: "Los datos se han exportado correctamente",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error al exportar:", error);
      toast({
        title: "Error al exportar",
        description: "No se pudieron exportar los datos",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Tabs isFitted variant="enclosed" colorScheme="blue">
      <TabList mb="1em">
        <Tab
          _selected={{
            color: colors.primary,
            borderColor: colors.primary,
            borderBottom: "none",
            bg: colors.cardBg,
          }}
          color={colors.textMuted}
          bg={colors.headerBg}
        >
          Resumen por {groupLevel}
        </Tab>
        <Tab
          _selected={{
            color: colors.primary,
            borderColor: colors.primary,
            borderBottom: "none",
            bg: colors.cardBg,
          }}
          color={colors.textMuted}
          bg={colors.headerBg}
        >
          Datos Completos
        </Tab>
      </TabList>

      <TabPanels>
        {/* Primera pestaña: Resumen */}
        <TabPanel p={0}>
          <Flex justify="flex-end" mb={4}>
            <Button
              leftIcon={<DownloadIcon />}
              colorScheme="green"
              onClick={handleExportSummary}
              size="sm"
            >
              Exportar a Excel
            </Button>
          </Flex>

          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "separate",
                borderSpacing: 0,
                borderRadius: "12px",
                overflow: "hidden",
                backgroundColor: colors.cardBg,
                boxShadow: colors.shadow,
              }}
            >
              <thead>
                <tr style={{ backgroundColor: colors.headerBg }}>
                  <th
                    style={{
                      padding: "16px",
                      textAlign: "left",
                      color: colors.text,
                      fontWeight: 600,
                      fontSize: "14px",
                      borderBottom: `1px solid ${colors.border}`,
                    }}
                  >
                    {groupLevel.toUpperCase()}
                  </th>
                  <th
                    style={{
                      padding: "16px",
                      textAlign: "right",
                      color: colors.text,
                      fontWeight: 600,
                      fontSize: "14px",
                      borderBottom: `1px solid ${colors.border}`,
                    }}
                  >
                    Diurno
                  </th>
                  <th
                    style={{
                      padding: "16px",
                      textAlign: "right",
                      color: colors.text,
                      fontWeight: 600,
                      fontSize: "14px",
                      borderBottom: `1px solid ${colors.border}`,
                    }}
                  >
                    Nocturno
                  </th>
                  <th
                    style={{
                      padding: "16px",
                      textAlign: "right",
                      color: colors.text,
                      fontWeight: 600,
                      fontSize: "14px",
                      borderBottom: `1px solid ${colors.border}`,
                    }}
                  >
                    Total
                  </th>
                </tr>
              </thead>
              <tbody>
                {dataSource.map((row, idx) => (
                  <tr
                    key={idx}
                    style={{
                      backgroundColor:
                        selectedLocation === row.name
                          ? colors.hover
                          : colors.cardBg,
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                    }}
                    onClick={() => onRowSelect && onRowSelect(row.name)}
                  >
                    <td
                      style={{
                        padding: "12px 16px",
                        borderBottom: `1px solid ${colors.border}`,
                        color: colors.text,
                      }}
                    >
                      {row.name}
                    </td>
                    <td
                      style={{
                        padding: "12px 16px",
                        textAlign: "right",
                        borderBottom: `1px solid ${colors.border}`,
                        color: colors.primary,
                        fontWeight: 500,
                      }}
                    >
                      {row.day.toLocaleString()}
                    </td>
                    <td
                      style={{
                        padding: "12px 16px",
                        textAlign: "right",
                        borderBottom: `1px solid ${colors.border}`,
                        color: colors.secondary,
                        fontWeight: 500,
                      }}
                    >
                      {row.night.toLocaleString()}
                    </td>
                    <td
                      style={{
                        padding: "12px 16px",
                        textAlign: "right",
                        borderBottom: `1px solid ${colors.border}`,
                        color: colors.accent,
                        fontWeight: 600,
                      }}
                    >
                      {row.total.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabPanel>

        {/* Segunda pestaña: Datos Completos */}
        <TabPanel p={0}>
          <Box mb={4}>
            <Flex gap={2}>
              <Input
                placeholder="Buscar por chasis, cantón, provincia o parroquia..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleSearch()}
              />
              <Button
                leftIcon={<SearchIcon />}
                colorScheme="blue"
                onClick={handleSearch}
                isLoading={isLoading}
              >
                Buscar
              </Button>
              <Button
                leftIcon={<DownloadIcon />}
                colorScheme="green"
                onClick={handleExportComplete}
                isLoading={isExporting}
              >
                Exportar a Excel
              </Button>
            </Flex>
          </Box>

          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "separate",
                borderSpacing: 0,
                borderRadius: "12px",
                overflow: "hidden",
                backgroundColor: colors.cardBg,
                boxShadow: colors.shadow,
              }}
            >
              <thead>
                <tr style={{ backgroundColor: colors.headerBg }}>
                  <th
                    style={{
                      padding: "16px",
                      textAlign: "left",
                      color: colors.text,
                      fontWeight: 600,
                      fontSize: "14px",
                      borderBottom: `1px solid ${colors.border}`,
                    }}
                  >
                    Chasis (VIN)
                  </th>
                  <th
                    style={{
                      padding: "16px",
                      textAlign: "left",
                      color: colors.text,
                      fontWeight: 600,
                      fontSize: "14px",
                      borderBottom: `1px solid ${colors.border}`,
                    }}
                  >
                    Provincia (D/N)
                  </th>
                  <th
                    style={{
                      padding: "16px",
                      textAlign: "left",
                      color: colors.text,
                      fontWeight: 600,
                      fontSize: "14px",
                      borderBottom: `1px solid ${colors.border}`,
                    }}
                  >
                    Cantón (D/N)
                  </th>
                  <th
                    style={{
                      padding: "16px",
                      textAlign: "left",
                      color: colors.text,
                      fontWeight: 600,
                      fontSize: "14px",
                      borderBottom: `1px solid ${colors.border}`,
                    }}
                  >
                    Parroquia (D/N)
                  </th>
                  <th
                    style={{
                      padding: "16px",
                      textAlign: "left",
                      color: colors.text,
                      fontWeight: 600,
                      fontSize: "14px",
                      borderBottom: `1px solid ${colors.border}`,
                    }}
                  >
                    Coordenadas Diurno
                  </th>
                  <th
                    style={{
                      padding: "16px",
                      textAlign: "left",
                      color: colors.text,
                      fontWeight: 600,
                      fontSize: "14px",
                      borderBottom: `1px solid ${colors.border}`,
                    }}
                  >
                    Coordenadas Nocturno
                  </th>
                </tr>
              </thead>
              <tbody>
                {completeData.data.map((row) => (
                  <tr
                    key={row.id}
                    style={{
                      backgroundColor: colors.cardBg,
                      transition: "all 0.2s ease",
                    }}
                  >
                    <td
                      style={{
                        padding: "12px 16px",
                        borderBottom: `1px solid ${colors.border}`,
                        color: colors.text,
                      }}
                    >
                      {row.vin}
                    </td>
                    <td
                      style={{
                        padding: "12px 16px",
                        borderBottom: `1px solid ${colors.border}`,
                        color: colors.text,
                      }}
                    >
                      {row.day_province} / {row.night_province}
                    </td>
                    <td
                      style={{
                        padding: "12px 16px",
                        borderBottom: `1px solid ${colors.border}`,
                        color: colors.text,
                      }}
                    >
                      {row.day_canton} / {row.night_canton}
                    </td>
                    <td
                      style={{
                        padding: "12px 16px",
                        borderBottom: `1px solid ${colors.border}`,
                        color: colors.text,
                      }}
                    >
                      {row.day_parroquia} / {row.night_parroquia}
                    </td>
                    <td
                      style={{
                        padding: "12px 16px",
                        borderBottom: `1px solid ${colors.border}`,
                        color: colors.primary,
                      }}
                    >
                      {row.day_lat}, {row.day_lng}
                    </td>
                    <td
                      style={{
                        padding: "12px 16px",
                        borderBottom: `1px solid ${colors.border}`,
                        color: colors.secondary,
                      }}
                    >
                      {row.night_lat}, {row.night_lng}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Paginación */}
          <Flex justify="center" align="center" mt={4} gap={2}>
            <IconButton
              icon={<ChevronLeftIcon />}
              onClick={() => handlePageChange(currentPage - 1)}
              isDisabled={currentPage === 1}
              aria-label="Página anterior"
            />
            <Text>
              Página {currentPage} de {completeData.totalPages}
            </Text>
            <IconButton
              icon={<ChevronRightIcon />}
              onClick={() => handlePageChange(currentPage + 1)}
              isDisabled={currentPage === completeData.totalPages}
              aria-label="Página siguiente"
            />
          </Flex>
        </TabPanel>
      </TabPanels>
    </Tabs>
  );
};

export default VehicleLocationsTable;
